// WhatWeBuy.jsx - The full list, and just as importantly, the honest list of what
// we do not buy. Being told "we buy everything" is what makes people suspicious,
// and the second list is what makes the first one believable.

import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, faqSchema } from "../lib/seo.jsx";
import { useSpot } from "../lib/hooks.js";
import { money, METAL_LABELS } from "../lib/format.js";
import { Panel } from "../ui/kit.jsx";
import { Reveal, Stagger, StaggerBoxed } from "../ui/motion.jsx";
import {
  ChainCoil, RingSolitaire, EarringPair, Flatware, CoinStack,
  BarStack, PocketWatch, DentalCrown,
} from "../ui/illustrations.jsx";
import { ArrowRight, Check, Close } from "../ui/icons.jsx";
import { PageHead, CtaBand, FaqList, BuyMarquee, MobileCta } from "./parts.jsx";

////////// WHAT WE TAKE //////////

const CATEGORIES = [
  {
    metal: "gold",
    title: "Gold",
    lead: "Any karat from 9K up, in any condition. Broken is worth the same per gram as unbroken.",
    art: [ChainCoil, RingSolitaire, DentalCrown],
    items: [
      "Chains, bracelets and pendants, including snapped ones",
      "Rings of every kind, including class rings and signet rings",
      "Single earrings and orphan halves of a pair",
      "Dental gold, crowns and bridges, including with tooth material still attached",
      "Watch cases and bracelets, gold pocket watch cases",
      "Coins and bars, bullion and numismatic",
      "Charms, cufflinks, tie clips, brooches, lockets",
      "Bench sweeps, polishing dust and casting sprue from the trade",
    ],
  },
  {
    metal: "silver",
    title: "Silver",
    lead: "Sterling and above. Flatware sets are usually worth far more than people expect, because they are heavy.",
    art: [Flatware, CoinStack, BarStack],
    items: [
      "Sterling flatware, complete services or single pieces",
      "Hollowware: trays, bowls, candlesticks, tea services",
      ".925 jewellery of any kind",
      "Pre-1965 US dimes, quarters and half dollars",
      "1965 to 1970 half dollars, 40 percent silver",
      "Bars, rounds and generic bullion",
      "Sterling picture frames, dresser sets, baby cups",
      "Photographic and industrial silver scrap",
    ],
  },
  {
    metal: "platinum",
    title: "Platinum and palladium",
    lead: "Denser than gold, so a small piece is worth checking. Marked PLAT, PT, 950 or PD.",
    art: [RingSolitaire, PocketWatch, EarringPair],
    items: [
      "Ring settings and mountings, with or without stones",
      "Wedding and eternity bands",
      "Laboratory crucibles, wire and thermocouples",
      "Dental alloy",
      "Catalyst and industrial scrap",
      "Palladium jewellery and white gold alloy containing palladium",
    ],
  },
];

////////// WHAT WE DO NOT //////////

const NOT_BUYING = [
  ["Gold plated and gold filled", "Marked GP, GF, RGP, HGE or 1/20. There is a real but microscopic amount of gold and it costs more to recover than it is worth."],
  ["Silver plate", "EPNS, A1, IS, triple plate, and anything sold as German or nickel silver. No recoverable silver content."],
  ["Costume jewellery", "Even the good vintage pieces. They can be worth real money, but as objects, not as metal, and we are not the right buyer."],
  ["Loose diamonds and gemstones on their own", "We deduct and return stones rather than valuing them. A specialist will do far better for you than we would."],
  ["Watches with value beyond the metal", "If it is a Rolex, a Patek or anything similar, please do not send it to us to be melted. Sell the watch."],
];

const FAQS = [
  {
    question: "Do you buy broken jewellery?",
    answer: "Yes, and it is most of what arrives. Value is worked out from metal content, so a snapped chain is worth exactly the same per gram as one that still fastens. Do not repair anything before sending it.",
  },
  {
    question: "Can I send dental gold with tooth still attached?",
    answer: "Yes. Send it as it is. We remove and dispose of the tooth material ourselves, which is normal and is not charged for.",
  },
  {
    question: "What happens to the stones in my rings?",
    answer: "The stone weight is deducted so you are never paid metal rates for a stone, and the stones come back with your items unless you ask us to look at them separately. Very small melee stones are usually not worth removing and we will say so.",
  },
  {
    question: "I have a mixed bag and I do not know what any of it is.",
    answer: "That is the most common thing we receive. Send the lot. We sort, test and itemise everything, and anything that turns out to be plated or costume comes straight back to you with the rest.",
  },
];

export default function WhatWeBuy() {
  const { spot } = useSpot();

  return (
    <>
      <Seo
        title="What we buy"
        path="/what-we-buy"
        description="Gold, silver, platinum and palladium in almost any form: broken jewellery, single earrings, dental gold, sterling flatware, coins, bars and industrial scrap. Plus an honest list of what we do not buy."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "What we buy", to: "/what-we-buy" }])} />
      <JsonLd data={faqSchema(FAQS)} />

      <PageHead
        eyebrow="What we buy"
        title="Broken counts. Single earrings count. Dental gold counts."
        lede="Everything is valued on its precious metal content, so condition is irrelevant. If it is gold, silver, platinum or palladium and it is yours, we will weigh it."
      >
        <Link to="/get-a-kit" className="btn btn-primary btn-lg">Get a free kit <ArrowRight size={17} /></Link>
      </PageHead>

      <BuyMarquee className="mb-2" />

      <div className="shell band-tight space-y-12 md:space-y-16">
        {CATEGORIES.map((category, index) => (
          <section key={category.metal} className="grid gap-7 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14 border-t border-rule pt-9 md:pt-10">
            <Reveal>
              <h2 className="display display-lg text-ink">{category.title}</h2>
              <p className="hint mt-4 leading-relaxed measure">{category.lead}</p>

              {/* Three small drawings per category, so the page is never a wall
                  of bullets. */}
              <Stagger as="div" step={90} start={120} className="flex gap-2 mt-6 max-w-[19rem]">
                {category.art.map((Drawing, artIndex) => (
                  <Drawing key={artIndex} className="w-1/3" />
                ))}
              </Stagger>

              {spot?.perOunce?.[category.metal] && (
                <p className="mt-5 pt-5 border-t border-rule-soft num text-[14px] text-muted">
                  {METAL_LABELS[category.metal]} spot today{" "}
                  <span className="text-ink font-semibold">{money(spot.perOunce[category.metal])}</span> per troy ounce
                </p>
              )}
            </Reveal>

            <StaggerBoxed
              as="ul"
              step={45}
              from="left"
              start={index === 0 ? 0 : 80}
              className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 self-start"
            >
              {category.items.map((item) => (
                <li key={item} className="flex gap-2.5 text-[14.5px] sm:text-[15px] text-ink-soft leading-snug">
                  <Check size={16} className="text-bronze shrink-0 mt-1" />
                  {item}
                </li>
              ))}
            </StaggerBoxed>
          </section>
        ))}
      </div>

      {/* The honest half. */}
      <section className="border-y border-rule bg-surface/40 band">
        <div className="shell grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
          <Reveal>
            <p className="eyebrow mb-3">Just as important</p>
            <h2 className="display display-lg text-ink">What we will not buy.</h2>
            <p className="hint mt-4 leading-relaxed measure">
              If you send any of these we will tell you plainly and post them straight back at our cost.
              We would rather say no than pay you a token amount for something that is worth more elsewhere.
            </p>
          </Reveal>

          <StaggerBoxed as="dl" step={70} className="divide-y divide-rule">
            {NOT_BUYING.map(([title, body]) => (
              <div key={title} className="py-5 first:pt-0 flex gap-3.5">
                <Close size={17} className="text-faint shrink-0 mt-1" />
                <div>
                  <dt className="font-semibold text-[15px] text-ink">{title}</dt>
                  <dd className="hint mt-1 leading-relaxed">{body}</dd>
                </div>
              </div>
            ))}
          </StaggerBoxed>
        </div>
      </section>

      <section className="shell band">
        <div className="grid gap-9 lg:grid-cols-2 lg:gap-16 items-start">
          <Reveal>
            <h2 className="display display-lg text-ink">Not sure what you have?</h2>
            <p className="lede mt-4 measure text-muted">
              Neither are most people. Send it anyway. Sorting is our job, and a mixed bag is the single
              most common thing that arrives here.
            </p>
            <div className="flex flex-col xs:flex-row flex-wrap gap-3 mt-6">
              <Link to="/tools/karat-chart" className="btn btn-outline">Read the hallmark chart</Link>
              <Link to="/get-a-kit" className="btn btn-primary">Get a free kit</Link>
            </div>
          </Reveal>

          <Reveal from="right" delay={120}>
            <Panel className="p-5 sm:p-6">
              <p className="eyebrow mb-3">Common questions</p>
              <FaqList items={FAQS} className="border-t-0" defaultOpen={0} />
            </Panel>
          </Reveal>
        </div>
      </section>

      <CtaBand />
      <MobileCta />
    </>
  );
}
